import {
  Box,
  Button,
  Chip,
  FormHelperText,
  IconButton,
  InputLabel,
  Stack,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "screens/Campaigns/CreateCampaign/Context";
import {
  convertESTtoUserLocalTime,
  convertToEstTime,
  isTruthy,
} from "helpers/methods";
import { cloneDeep } from "lodash";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

const ScheduleSectionTestingVariable = () => {
  const { managingStep, setManagingStep, submitCampaign, setSubmitCampaign, saveAsDraft } =
    useContext(CampaignContext);
  const [saving, setSaving] = useState<boolean>(false);
  const [formFields, setFormFields] = useState<
    {
      date: { value: Date | null; error: string };
      time: { value: Date | null; error: string };
    }[]
  >([]);

  useEffect(() => {
    if (managingStep === "sending-time") {
      updateFormFields();
    }
  }, [managingStep]);

  const updateFormFields = () => {
    const now = new Date();
    const minAllowedTime = new Date(now.getTime() + 5 * 60 * 1000); // Current time + 5 minutes

    const sendingTimes = Array.from({
      length: submitCampaign.abTestDetails?.testingVariants ?? 0,
    }).map((_, i) => {
      if (isTruthy(submitCampaign.abTestDetails?.testingValues[i])) {
        const estTime = submitCampaign.abTestDetails?.testingValues[i]!;
        const localDateTime = moment
          .tz(estTime, "MM/DD/YYYY HH:mm:ss", "America/New_York")
          .local()
          .toDate();

        return {
          date: { value: localDateTime, error: "" },
          time: { value: localDateTime, error: "" },
        };
      }

      return {
        date: { value: minAllowedTime, error: "" },
        time: { value: minAllowedTime, error: "" },
      };
    });

    setFormFields(sendingTimes);
  };

  const handleSave = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    clonedFormFields.forEach((field) => {
      if (!field.date.value) {
        error = true;
        field.date.error = "Please select the date.";
      }
      if (moment().isAfter(field.date.value, "date")) {
        error = true;
        field.date.error =
          "Selected date is already past. Please select a future date.";
      }
      if (!field.time.value) {
        error = true;
        field.time.error = "Please select the time.";
      }
      // if (moment().isAfter(field.time.value, "milliseconds")) {
      //   error = true;
      //   field.time.error =
      //     "Selected time is already past. Please select future time.";
      // }
    });
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSaving(true);
    const sendingTimes = clonedFormFields.map((field) => {
      const date = new Date(field.date.value!);
      const time = new Date(field.time.value!);
      const scheduleTime = moment(date)
        .set({
          hour: time.getHours(),
          minute: time.getMinutes(),
          second: time.getSeconds(),
        })
        .toString();
      return convertToEstTime(scheduleTime, "MM/DD/yyyy HH:mm:ss");
    });
    setSubmitCampaign({
      ...submitCampaign,
      abTestDetails: {
        ...submitCampaign.abTestDetails,
        testingValues: sendingTimes,
      },
    });
    await saveAsDraft(submitCampaign);
    setSaving(false);
    setManagingStep("");
  };

  const stepCompletion = () => {
    const values = submitCampaign.abTestDetails?.testingValues;
    const expectedVariants = submitCampaign.abTestDetails?.testingVariants;
    return (
      Array.isArray(values) &&
      values.length === expectedVariants &&
      values.every((val) => val !== null && val !== undefined && val !== "")
    );
  };  

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">Sending time</Typography>
                <Typography variant="body1">
                  Set the sending time for the campaign
                </Typography>
              </Stack>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                {formFields.map((field, index: number) => (
                  <Stack spacing={1}>
                    <InputLabel required>{`Sending time (Version ${
                      index + 1
                    })`}</InputLabel>
                    <Stack direction="row" spacing={1}>
                      <Stack>
                        <DatePicker
                          value={
                            field.date.value ? new Date(field.date.value) : null
                          }
                          onChange={(newValue) => {
                            const clonedFormFields = cloneDeep(formFields);
                            clonedFormFields[index] = {
                              ...clonedFormFields[index],
                              date: {
                                value: newValue ?? null,
                                error: "",
                              },
                            };
                            setFormFields(clonedFormFields);
                          }}
                          slots={{
                            textField: TextField,
                          }}
                          disablePast
                          slotProps={{
                            textField: {
                              sx: {
                                width: "fit-content", // Set the width to fit the content or adjust as needed
                                border: 0, // Add a border
                                borderRadius: "4px", // Rounded corners
                              },
                              variant: "outlined",
                              fullWidth: true,
                              placeholder: "Select the date",
                            },
                          }}
                        />
                        {isTruthy(field.date.error) && (
                          <FormHelperText error>
                            {field.date.error}
                          </FormHelperText>
                        )}
                      </Stack>
                      <Stack>
                        <TimePicker
                          value={
                            field.time.value ? new Date(field.time.value) : null
                          }
                          onChange={(newValue) => {
                            const clonedFormFields = cloneDeep(formFields);
                            clonedFormFields[index] = {
                              ...clonedFormFields[index],
                              time: {
                                value: newValue ?? null,
                                error: "",
                              },
                            };
                            setFormFields(clonedFormFields);
                          }}
                          slots={{
                            textField: TextField,
                          }}
                          minTime={
                            field.date.value &&
                            new Date(field.date.value).toDateString() ===
                              new Date().toDateString()
                              ? new Date()
                              : undefined
                          }
                          slotProps={{
                            textField: {
                              sx: {
                                width: "fit-content", // Set the width to fit the content or adjust as needed
                                border: 0, // Add a border
                                borderRadius: "4px", // Rounded corners
                              },
                              variant: "outlined",
                              fullWidth: true,
                              placeholder: "Select the time",
                            },
                          }}
                        />
                        {isTruthy(field.time.error) && (
                          <FormHelperText error>
                            {field.time.error}
                          </FormHelperText>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                ))}
              </LocalizationProvider>
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={() => setManagingStep("")}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setManagingStep("")}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "sending-time";
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4">Sending time</Typography>
              <Chip label={"Testing Variable"} />
            </Stack>
            <Stack spacing={1}>
              <Typography variant="body1">
                The versions would be sent at the scheduled time
              </Typography>
              {Array.from({
                length: submitCampaign.abTestDetails?.testingVariants ?? 0,
              }).map((_, i) => {
                return (
                  <Typography variant="body2" color="text.secondary" pl={3}>
                    Version {i + 1}:{" "}
                    {submitCampaign.abTestDetails?.testingValues[i]!
                      ? convertESTtoUserLocalTime(
                          submitCampaign.abTestDetails?.testingValues[i]!
                        )
                      : ""}
                  </Typography>
                );
              })}
            </Stack>
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("sending-time");
            }}
            disabled={onAnotherStep}
          >
            Edit Sending Time
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getPage = () => {
    if (managingStep === "sending-time") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return (
    <>
      <Box px={3}>{getPage()}</Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default ScheduleSectionTestingVariable;
